import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productAPI, categoryAPI } from '../../services/api';
import { Pagination, EmptyState } from '../../components/common';
import './CataloguePage.css';

const GENDERS = [
  { value: '',        label: 'Tous' },
  { value: 'homme',  label: '👔 Homme' },
  { value: 'femme',  label: '👗 Femme' },
  { value: 'enfant', label: '🧒 Enfant' },
  { value: 'unisexe',label: '👕 Unisexe' },
];

const SORT_OPTIONS = [
  { value: '',              label: 'Pertinence' },
  { value: 'newest',        label: 'Nouveautés' },
  { value: 'price_asc',     label: 'Prix croissant' },
  { value: 'price_desc',    label: 'Prix décroissant' },
  { value: 'name_az',       label: 'A → Z' },
];

const SORT_MAP = {
  newest:     { sort: 'created_at', order: 'desc' },
  price_asc:  { sort: 'base_price', order: 'asc'  },
  price_desc: { sort: 'base_price', order: 'desc' },
  name_az:    { sort: 'name',       order: 'asc'  },
};

const CataloguePage = () => {
  const { categorySlug } = useParams();

  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta,       setMeta]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [search,     setSearch]     = useState('');
  const [searchVal,  setSearchVal]  = useState('');
  const [gender,     setGender]     = useState('');
  const [sortVal,    setSortVal]    = useState('');

  const activeCatId = categories.find(c => c.slug === categorySlug)?.id;

  const hasActiveFilters = gender !== '' || sortVal !== '';

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: 12, published: true };
    if (activeCatId)  params.category = activeCatId;
    if (search)       params.search   = search;
    if (gender)       params.gender   = gender;
    if (sortVal && SORT_MAP[sortVal]) {
      params.sort  = SORT_MAP[sortVal].sort;
      params.order = SORT_MAP[sortVal].order;
    }
    productAPI.list(params)
      .then(({ data }) => { setProducts(data.data || []); setMeta(data.meta); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, activeCatId, search, gender, sortVal]);

  useEffect(() => {
    categoryAPI.list()
      .then(({ data }) => {
        const active = (data.data || [])
          .filter(c => c.is_active)
          .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
        setCategories(active);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchVal); setPage(1); };
  const resetFilters = () => { setGender(''); setSortVal(''); setPage(1); };

  return (
    <div className="catalogue-page">
      <div className="catalogue-header">
        <div className="container">
          <h1 className="catalogue-title">Notre Catalogue</h1>
          <p className="catalogue-subtitle">Trouvez le vêtement idéal et personnalisez-le directement dans notre studio.</p>
          <form onSubmit={handleSearch} className="catalogue-search">
            <div className="search-bar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                type="search"
                placeholder="Rechercher un produit…"
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                className="form-input"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-md">Rechercher</button>
          </form>
        </div>
      </div>

      <div className="container catalogue-layout">
        {/* Sidebar */}
        <aside className="catalogue-filters">

          {/* Catégories */}
          <h3 className="filter-title">Catégories</h3>
          <nav className="category-nav" style={{ marginBottom: 'var(--space-xl)' }}>
            <Link to="/catalogue" className={`cat-link${!categorySlug ? ' active' : ''}`} onClick={() => setPage(1)}>
              <span>Tous les produits</span>
              <span className="cat-count">{meta?.total || ''}</span>
            </Link>
            {categories.map((cat) => (
              <Link key={cat.id} to={`/catalogue/${cat.slug}`} className={`cat-link${categorySlug === cat.slug ? ' active' : ''}`} onClick={() => setPage(1)}>
                <span>{cat.name}</span>
              </Link>
            ))}
          </nav>

          {/* Genre */}
          <h3 className="filter-title">Genre</h3>
          <div className="filter-chips" style={{ marginBottom: 'var(--space-xl)' }}>
            {GENDERS.map(g => (
              <button
                key={g.value}
                onClick={() => { setGender(g.value); setPage(1); }}
                className={`filter-chip${gender === g.value ? ' active' : ''}`}
              >
                {g.label}
              </button>
            ))}
          </div>

          {/* Tri */}
          <h3 className="filter-title">Trier par</h3>
          <div className="filter-chips" style={{ marginBottom: 'var(--space-xl)' }}>
            {SORT_OPTIONS.map(s => (
              <button
                key={s.value}
                onClick={() => { setSortVal(s.value); setPage(1); }}
                className={`filter-chip${sortVal === s.value ? ' active' : ''}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Reset */}
          {hasActiveFilters && (
            <button onClick={resetFilters} className="filter-reset">
              ✕ Réinitialiser les filtres
            </button>
          )}
        </aside>

        {/* Grille produits */}
        <div className="catalogue-content">
          {/* Barre résultats */}
          <div className="catalogue-toolbar">
            <span className="catalogue-count">
              {loading ? '…' : `${meta?.total || 0} produit${(meta?.total || 0) > 1 ? 's' : ''}`}
              {gender && <span className="active-filter-tag">{GENDERS.find(g => g.value === gender)?.label}</span>}
              {categorySlug && <span className="active-filter-tag">{categories.find(c => c.slug === categorySlug)?.name}</span>}
            </span>
          </div>

          {loading ? (
            <div className="page-loader"><div className="spinner spinner-lg" /></div>
          ) : !products.length ? (
            <EmptyState icon="🔍" title="Aucun produit trouvé" description="Essayez une autre recherche ou modifiez les filtres." />
          ) : (
            <>
              <div className="products-grid-cat fade-in">
                {products.map((p) => (
                  <Link key={p.id} to={p.slug ? `/produit/${p.slug}` : `/catalogue`} className="product-card-cat">
                    <div className="product-card-img">
                      {p.main_image
                        ? <img src={p.main_image} alt={p.name} loading="lazy" />
                        : <div className="product-placeholder">👕</div>
                      }
                      {p.gender && <span className="product-gender-badge">{GENDERS.find(g=>g.value===p.gender)?.label}</span>}
                      <div className="product-card-overlay"><span>Personnaliser →</span></div>
                    </div>
                    <div className="product-card-body">
                      <span className="product-cat-tag">{p.category_name}</span>
                      <h3 className="product-card-name">{p.name}</h3>
                      <p className="product-ref">Réf. {p.reference}</p>
                      <div className="product-colors-row">
                        {(p.available_colors || []).slice(0, 6).map((c, ci) => (
                          <span key={c.name+ci} className="color-dot" style={{ ...(c.hexes?.length > 1
                            ? { background: `conic-gradient(${c.hexes.map((h,i)=>`${h} ${i*360/c.hexes.length}deg ${(i+1)*360/c.hexes.length}deg`).join(',')})` }
                            : { background: c.hex }
                          )}} title={c.name} />
                        ))}
                        {(p.available_colors || []).length > 6 && (
                          <span className="color-more">+{(p.available_colors || []).length - 6}</span>
                        )}
                      </div>
                      <div className="product-card-footer">
                        <span className="product-card-price">Dès {(parseFloat(p.base_price || 0) || 0).toFixed(2)} €</span>
                        <span className="product-card-cta">Voir →</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="catalogue-pagination">
                <Pagination meta={meta} onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CataloguePage;
